import { useEffect, useMemo, useRef, useState } from 'react';

/**
 * 多選下拉（標籤 + 勾選清單），空陣列代表「全部」不篩選。
 */
export default function MultiSelectDropdown({
  id,
  label,
  options,
  value = [],
  onChange,
  allLabel = '全部',
  placeholder = '請選擇',
  className = '',
}) {
  const [open, setOpen] = useState(false);
  const [keyword, setKeyword] = useState('');
  const rootRef = useRef(null);
  const inputRef = useRef(null);

  const selectedValues = Array.isArray(value) ? value : [];
  const isAll = selectedValues.length === 0;

  const optionMap = useMemo(
    () => Object.fromEntries(options.map((item) => [item.value, item.label])),
    [options]
  );

  const filteredOptions = useMemo(() => {
    const text = keyword.trim().toLowerCase();
    if (!text) {
      return options;
    }
    return options.filter(
      (item) =>
        item.label.toLowerCase().includes(text) || String(item.value).toLowerCase().includes(text)
    );
  }, [keyword, options]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        setOpen(false);
        setKeyword('');
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  function openDropdown() {
    setOpen(true);
    requestAnimationFrame(() => inputRef.current?.focus());
  }

  function handleSelectAll() {
    onChange?.([]);
    setKeyword('');
  }

  function toggleOption(optionValue) {
    if (isAll) {
      onChange?.([optionValue]);
      return;
    }
    if (selectedValues.includes(optionValue)) {
      onChange?.(selectedValues.filter((item) => item !== optionValue));
      return;
    }
    onChange?.([...selectedValues, optionValue]);
  }

  function removeTag(optionValue) {
    onChange?.(selectedValues.filter((item) => item !== optionValue));
  }

  return (
    <div className={`multi-select ${className}`} ref={rootRef}>
      {label ? (
        <label className="multi-select__label" htmlFor={id}>
          {label}
        </label>
      ) : null}

      <div
        className={`multi-select__control${open ? ' multi-select__control--open' : ''}`}
        onClick={openDropdown}
        role="combobox"
        aria-expanded={open}
        aria-haspopup="listbox"
      >
        <div className="multi-select__tags">
          {isAll ? (
            <span className="multi-select__tag">
              {allLabel}
              <button
                type="button"
                className="multi-select__tag-remove"
                aria-label={`清除${allLabel}`}
                onClick={(e) => {
                  e.stopPropagation();
                  handleSelectAll();
                }}
              >
                ×
              </button>
            </span>
          ) : (
            selectedValues.map((item) => (
              <span key={item} className="multi-select__tag">
                {optionMap[item] || item}
                <button
                  type="button"
                  className="multi-select__tag-remove"
                  aria-label="移除"
                  onClick={(e) => {
                    e.stopPropagation();
                    removeTag(item);
                  }}
                >
                  ×
                </button>
              </span>
            ))
          )}
          <input
            ref={inputRef}
            id={id}
            className="multi-select__input"
            type="text"
            value={keyword}
            placeholder={isAll && !open ? placeholder : ''}
            onChange={(e) => {
              setKeyword(e.target.value);
              setOpen(true);
            }}
            onFocus={() => setOpen(true)}
            onClick={(e) => e.stopPropagation()}
          />
        </div>
        <span className="multi-select__arrow" aria-hidden>
          ▾
        </span>
      </div>

      {open ? (
        <ul className="multi-select__menu" role="listbox" aria-multiselectable="true">
          <li>
            <button
              type="button"
              className={`multi-select__option${isAll ? ' multi-select__option--selected' : ''}`}
              onClick={handleSelectAll}
            >
              <span>{allLabel}</span>
              {isAll ? <span className="multi-select__check">✓</span> : null}
            </button>
          </li>
          {filteredOptions.length === 0 ? (
            <li className="multi-select__empty">無符合選項</li>
          ) : (
            filteredOptions.map((item) => {
              const selected = !isAll && selectedValues.includes(item.value);
              return (
                <li key={item.value}>
                  <button
                    type="button"
                    className={`multi-select__option${selected ? ' multi-select__option--selected' : ''}`}
                    onClick={() => toggleOption(item.value)}
                  >
                    <span>{item.label}</span>
                    {selected ? <span className="multi-select__check">✓</span> : null}
                  </button>
                </li>
              );
            })
          )}
        </ul>
      ) : null}
    </div>
  );
}
